import { memo } from "react";

import type { RealAssetResponse } from "@/apiClient";

import {
  getDisplaySegmentsForTemplatePath,
  realAssetTypeLabel,
  renderSegmentLabel,
  renderVariableSegmentBadgeLabel,
  type PathSegment,
} from "./folderConfig.utils";

const variableSegmentBadgeClasses = ({
  interactive = false,
  isActive = false,
}: {
  interactive?: boolean;
  isActive?: boolean;
}) =>
  [
    "rounded-lg border px-2.5 py-1.5 text-sm font-medium",
    isActive
      ? "border-[#0072CE] bg-[#1e3a5f] text-white shadow-[0_0_0_2px_rgba(0,114,206,0.14)]"
      : "border-[#1e3a5f] bg-[#1e3a5f] text-white",
    interactive ? "transition hover:opacity-90" : "",
  ]
    .filter(Boolean)
    .join(" ");

export function VariableSegmentBadge({
  segment,
  realAssetsById,
  title,
  onClick,
  ariaLabel,
  isActive = false,
}: {
  segment: PathSegment;
  realAssetsById: Map<string, RealAssetResponse>;
  title?: string;
  onClick?: () => void;
  ariaLabel?: string;
  isActive?: boolean;
}) {
  const label = renderVariableSegmentBadgeLabel(segment, realAssetsById);

  if (onClick) {
    return (
      <button
        type="button"
        className={variableSegmentBadgeClasses({
          interactive: true,
          isActive,
        })}
        title={title}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        {label}
      </button>
    );
  }

  return (
    <span
      className={variableSegmentBadgeClasses({
        isActive,
      })}
      title={title}
    >
      {label}
    </span>
  );
}

function TemplatePathPills({
  templatePath,
  realAssetsById,
  emptyLabel = "No directory pattern configured yet",
}: {
  templatePath: string;
  realAssetsById: Map<string, RealAssetResponse>;
  emptyLabel?: string;
}) {
  const segments = getDisplaySegmentsForTemplatePath(templatePath);

  if (segments.length === 0) {
    return <p className="text-sm text-grey-200">{emptyLabel}</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {segments.map((segment, index) => {
        const asset =
          segment.kind === "token" ? realAssetsById.get(segment.value) : null;

        return (
          <div
            key={`${segment.kind}-${segment.value}-${index}`}
            className="flex items-center gap-1.5"
          >
            {segment.kind === "token" ? (
              <VariableSegmentBadge
                segment={segment}
                realAssetsById={realAssetsById}
                title={
                  asset
                    ? `${asset.name} (${realAssetTypeLabel(asset.type)})`
                    : undefined
                }
              />
            ) : (
              <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm text-blue-400">
                {renderSegmentLabel(segment, realAssetsById)}
              </span>
            )}
            {index < segments.length - 1 ? (
              <span className="text-xs text-grey-100">/</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

export default memo(TemplatePathPills);
